import { useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  Building2,
  Check,
  CreditCard,
  Database,
  Download,
  ExternalLink,
  Loader2,
  Plus,
  RefreshCw,
  UserPlus,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Button } from '../components/Button';
import { Card, CardHeader } from '../components/Card';
import { Badge } from '../components/Badge';
import { InvoiceStatusBadge, isPayableInvoiceStatus, providerLabel } from '../components/InvoiceStatusBadge';
import { Money } from '../components/Money';
import { Skeleton } from '../components/Skeleton';
import { Avatar } from '../components/Avatar';
import { cn } from '../lib/cn';
import { formatDate, formatInt, timeAgo } from '../lib/format';
import { useActiveBusiness, useDashboard, useSubscription, useInvoices, usePosAccess } from '../api/hooks';
import { useSession } from '../stores/session';
import { openPos } from '../api/pos';
import { payInvoices, renewSubscription } from '../api/billing';
import { useQueryClient } from '@tanstack/react-query';

type SyncState = 'idle' | 'syncing' | 'just_synced';

/** Subscription status → badge tone/label (mirrors Subscription.tsx). */
const SUB_STATUS_TONE: Record<string, 'success' | 'info' | 'warning' | 'error' | 'neutral'> = {
  active: 'success',
  trial: 'info',
  past_due: 'warning',
  suspended: 'error',
  canceled: 'neutral',
};
const SUB_STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  trial: 'Trial',
  past_due: 'Past due',
  suspended: 'Suspended',
  canceled: 'Canceled',
};

export function Dashboard() {
  const { data: currentBusiness } = useActiveBusiness();
  const user = useSession((s) => s.user);
  const { data: dashboard } = useDashboard(currentBusiness?.id ?? '');
  const { data: subscription } = useSubscription(currentBusiness?.id ?? '');
  const { data: invoices } = useInvoices(currentBusiness?.id);
  const { blocked: posBlocked } = usePosAccess(currentBusiness?.id);

  const [syncState, setSyncState] = useState<SyncState>('idle');
  const [payingId, setPayingId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Early trial activation ("Activate now") — same rules as the Subscription page:
  // only for a trial sub with NO payable invoice (an open invoice must be paid via
  // the pay-invoices flow instead; the backend enforces the same guard).
  const hasOpenInvoice = (invoices ?? []).some((inv) => isPayableInvoiceStatus(inv.status));
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);

  async function activateNow() {
    if (!currentBusiness || activating) return;
    setActivating(true);
    setActivateError(null);
    try {
      const { gatewayPageURL } = await renewSubscription(currentBusiness.id);
      if (gatewayPageURL) window.location.href = gatewayPageURL;
      else setActivating(false);
    } catch (e) {
      setActivateError(e instanceof Error ? e.message : 'Could not start the payment. Please try again.');
      setActivating(false);
    }
  }

  async function payInvoice(businessId: string, invoiceId: string) {
    if (payingId) return;
    setPayingId(invoiceId);
    try {
      const { gatewayPageURL } = await payInvoices({ businessId, invoiceIds: [invoiceId] });
      if (gatewayPageURL) window.location.href = gatewayPageURL;
      else setPayingId(null);
    } catch {
      setPayingId(null);
    }
  }

  async function syncNow() {
    if (syncState === 'syncing') return;
    setSyncState('syncing');
    try {
      // Real refetch of the POS-metrics mirror. The central Laravel pushes each
      // business's business_metrics into Postgres every 15 min via the inbound HMAC
      // webhook; this pulls the latest snapshot. "Synced from POS" reflects the real
      // business_metrics.mirrored_at.
      await queryClient.invalidateQueries({ queryKey: ['dashboard', currentBusiness?.id] });
      setSyncState('just_synced');
      setTimeout(() => setSyncState('idle'), 2200);
    } catch {
      setSyncState('idle');
    }
  }

  if (!currentBusiness || !dashboard) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto" aria-busy="true">
        <div className="flex items-end justify-between gap-3 mb-8">
          <div className="space-y-2">
            <Skeleton className="h-3 w-12 rounded" />
            <Skeleton className="h-8 w-56 rounded" />
            <Skeleton className="h-4 w-72 rounded" />
          </div>
          <Skeleton className="h-10 w-28 rounded-md hidden sm:block" />
        </div>
        <Skeleton className="h-4 w-40 rounded mb-3" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-4 space-y-3">
              <Skeleton className="h-3 w-20 rounded" />
              <Skeleton className="h-7 w-24 rounded" />
              <Skeleton className="h-3 w-16 rounded" />
            </div>
          ))}
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          <Skeleton className="h-48 rounded-lg" />
          <Skeleton className="h-48 rounded-lg" />
        </div>
      </div>
    );
  }
  const baseKpis = dashboard.kpis;
  const recentActivity = dashboard.activity;
  const recentInvoices = (invoices ?? []).slice(0, 4);
  const todaysKpis = baseKpis; // mirroredFromPosAt is the real business_metrics.mirrored_at

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6 sm:mb-8">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-1.5">
            Home
          </p>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Welcome back, {user?.name.split(' ')[0] ?? 'there'}
          </h1>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
            Here's how {currentBusiness.name} is doing today.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link to="/billing">
            <Button variant="secondary" iconLeft={<Download className="w-4 h-4" />}>
              <span className="hidden sm:inline">Export</span>
            </Button>
          </Link>
          {!posBlocked && (
            <Button
              iconLeft={<ExternalLink className="w-4 h-4" />}
              onClick={() => openPos(currentBusiness.id, '/home')}
            >
              Open POS
            </Button>
          )}
        </div>
      </div>

      {/* KPI strip — mirrored from this business's Laravel POS via 15-min webhook */}
      <div className="flex items-center justify-between gap-2 mb-2 px-1 flex-wrap">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
          Today at {currentBusiness.name}
        </h2>
        <div className="inline-flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1.5 text-[11px] text-zinc-500 dark:text-zinc-400"
            title="KPIs mirror from the per-business Laravel POS into NestJS Postgres business_metrics every 15 minutes via HMAC-signed webhook. The dashboard never queries Laravel directly."
          >
            <Database className="w-3 h-3" />
            <span>
              Synced from POS · <span className="tabular-nums">{todaysKpis.mirroredFromPosAt ? timeAgo(todaysKpis.mirroredFromPosAt) : '—'}</span>
            </span>
          </span>
          <button
            type="button"
            onClick={syncNow}
            disabled={syncState === 'syncing'}
            aria-label="Sync KPIs from POS now"
            className={cn(
              'inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium transition',
              'border border-zinc-200 dark:border-zinc-700',
              syncState === 'just_synced'
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800'
                : 'bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800',
              syncState === 'syncing' && 'opacity-70 cursor-wait',
            )}
          >
            {syncState === 'syncing' ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                Syncing…
              </>
            ) : syncState === 'just_synced' ? (
              <>
                <Check className="w-3 h-3" />
                Synced
              </>
            ) : (
              <>
                <RefreshCw className="w-3 h-3" />
                Sync now
              </>
            )}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <KpiTile
          label="Sales today"
          value={<Money minor={todaysKpis.sales} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />}
          delta={todaysKpis.salesDelta}
          deltaSuffix="vs yesterday"
        />
        <KpiTile
          label="Orders"
          value={
            <span className="text-zinc-950 dark:text-zinc-50 tabular-nums">{formatInt(todaysKpis.orders)}</span>
          }
          delta={todaysKpis.ordersDelta}
          deltaSuffix="vs yesterday"
        />
        <KpiTile
          label="Cash collected"
          value={<Money minor={todaysKpis.cashCollected} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />}
          subtitle={`${todaysKpis.sales > 0 ? Math.round((todaysKpis.cashCollected / todaysKpis.sales) * 100) : 0}% of sales`}
        />
        <KpiTile
          label="Average order"
          value={<Money minor={todaysKpis.averageOrder} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />}
          delta={todaysKpis.averageOrderDelta}
          deltaSuffix="vs yesterday"
        />
      </div>

      {/* Subscription + Quick actions */}
      <div className="grid lg:grid-cols-3 gap-3 sm:gap-4 mb-6">
        <Card className="lg:col-span-2 p-5 sm:p-6">
          {subscription ? (
            <>
              <CardHeader
                title={
                  <div className="flex items-center gap-2.5">
                    <span>{subscription.plan} · </span>
                    <Money minor={subscription.amount} hideCents tone="neutral" />
                    <span className="text-zinc-500 dark:text-zinc-400 text-sm font-normal">/ {subscription.interval}</span>
                  </div>
                }
                subtitle={
                  <span className="tabular-nums">
                    Started {formatDate(subscription.startedAt, { compact: true })} ·
                    Renews {formatDate(subscription.renewsAt)}
                  </span>
                }
                action={<Badge tone={SUB_STATUS_TONE[subscription.status] ?? 'neutral'}>{SUB_STATUS_LABEL[subscription.status] ?? subscription.status}</Badge>}
              />

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-2">
                <UsageBar label="Locations" used={subscription.usage.locations.used} limit={subscription.usage.locations.limit} />
                <UsageBar label="Users" used={subscription.usage.users.used} limit={subscription.usage.users.limit} />
                <UsageBar label="Products" used={subscription.usage.products.used} limit={subscription.usage.products.limit} compact />
              </div>

              <div className="mt-5 pt-5 border-t border-zinc-200 dark:border-zinc-700 flex flex-wrap items-center gap-2">
                {subscription.status === 'trial' && !hasOpenInvoice && (
                  <Button
                    size="sm"
                    loading={activating}
                    iconLeft={<CreditCard className="w-3.5 h-3.5" />}
                    onClick={() => void activateNow()}
                  >
                    Activate now — pay ৳{Math.round(subscription.amount / 100).toLocaleString('en-US')}
                  </Button>
                )}
                <Link to="/billing"><Button variant="ghost" size="sm">View invoices</Button></Link>
                <Link to="/subscription"><Button variant="ghost" size="sm">Subscription details</Button></Link>
              </div>
              {subscription.status === 'trial' && !hasOpenInvoice && (
                <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
                  Activating starts your paid {subscription.interval === 'year' ? 'year' : 'month'} today and ends the
                  free trial — your renewal date resets to one {subscription.interval === 'year' ? 'year' : 'month'} from today.
                </p>
              )}
              {activateError && <p className="mt-2 text-xs text-rose-600 dark:text-rose-400">{activateError}</p>}
            </>
          ) : (
            <>
              <CardHeader
                title="No active subscription"
                subtitle="This business doesn't have an active plan yet."
                action={<Badge tone="neutral">Inactive</Badge>}
              />
              <div className="mt-5 pt-5 border-t border-zinc-200 dark:border-zinc-700 flex flex-wrap items-center gap-2">
                <Link to="/billing"><Button variant="ghost" size="sm">View invoices</Button></Link>
              </div>
            </>
          )}
        </Card>

        <Card className="p-5 sm:p-6">
          <CardHeader title="Quick actions" />
          <div className="space-y-1 -mx-2.5">
            <QuickAction to="/businesses/new" icon={<Plus className="w-4 h-4" />} label="New business" />
            <QuickAction to="/team" icon={<UserPlus className="w-4 h-4" />} label="Invite team member" />
            <QuickAction to="/businesses" icon={<Building2 className="w-4 h-4" />} label="Manage businesses" />
            <QuickAction to="/billing" icon={<Download className="w-4 h-4" />} label="Download invoices" />
            <QuickAction to="/subscription" icon={<CreditCard className="w-4 h-4" />} label="Update payment" />
          </div>
        </Card>
      </div>

      {/* Recent invoices + activity */}
      <div className="grid lg:grid-cols-3 gap-3 sm:gap-4">
        <Card className="lg:col-span-2 overflow-hidden">
          <div className="px-5 sm:px-6 pt-5 sm:pt-6 pb-4 flex items-end justify-between gap-3">
            <CardHeader title="Recent invoices" subtitle="Last 4 months" className="mb-0" />
            <Link to="/billing">
              <Button variant="ghost" size="sm" iconRight={<ArrowUp className="w-3 h-3 rotate-45" />}>
                View all
              </Button>
            </Link>
          </div>

          <div className="overflow-x-auto nice-scroll border-t border-zinc-200 dark:border-zinc-700">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
                <tr>
                  <th className="text-left font-medium px-5 sm:px-6 py-3">Invoice</th>
                  <th className="text-left font-medium px-4 py-3">Date</th>
                  <th className="text-right font-medium px-4 py-3">Amount</th>
                  <th className="text-left font-medium px-4 py-3 hidden sm:table-cell">Method</th>
                  <th className="text-left font-medium px-5 sm:px-6 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
                {recentInvoices.map((inv) => (
                  <tr key={inv.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                    <td className="px-5 sm:px-6 py-3 mono text-xs text-zinc-700 dark:text-zinc-200 whitespace-nowrap">
                      <Link to="/billing/$id" params={{ id: inv.id }} className="hover:text-brand dark:hover:text-brand-dm">
                        {inv.number}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200 whitespace-nowrap tabular-nums">
                      {formatDate(inv.date, { compact: true })}
                    </td>
                    <td className="px-4 py-3 text-right text-zinc-950 dark:text-zinc-50">
                      <Money minor={inv.amount} tone="neutral" />
                    </td>
                    <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 hidden sm:table-cell">
                      {providerLabel(inv.method)}
                    </td>
                    <td className="px-5 sm:px-6 py-3">
                      <div className="flex items-center gap-2.5">
                        <InvoiceStatusBadge status={inv.status} />
                        {isPayableInvoiceStatus(inv.status) && (
                          <Button
                            size="sm"
                            loading={payingId === inv.id}
                            onClick={() => void payInvoice(inv.businessId, inv.id)}
                            iconLeft={<CreditCard className="w-3.5 h-3.5" />}
                          >
                            Pay now
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="p-5 sm:p-6">
          <CardHeader title="Recent activity" subtitle="Last few hours" />
          <ul className="space-y-3 -mx-2">
            {recentActivity.map((a) => (
              <li key={a.id} className="flex items-start gap-3 px-2">
                <Avatar name={a.description} size="sm" className="mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-900 dark:text-zinc-100 truncate">
                    {a.description}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                    <span className="tabular-nums">{timeAgo(a.date)}</span>
                    {a.meta && (
                      <>
                        <span>·</span>
                        <span>{a.meta}</span>
                      </>
                    )}
                  </div>
                </div>
                {a.amount !== undefined && (
                  <div className="text-sm flex-shrink-0">
                    <Money minor={a.amount} />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── */

function KpiTile({
  label,
  value,
  delta,
  deltaSuffix,
  subtitle,
}: {
  label: string;
  value: React.ReactNode;
  delta?: number;
  deltaSuffix?: string;
  subtitle?: string;
}) {
  const positive = (delta ?? 0) >= 0;
  return (
    <Card className="p-4 sm:p-5">
      <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
        {label}
      </div>
      <div className="mt-1.5 text-2xl sm:text-3xl font-semibold">{value}</div>
      {delta !== undefined ? (
        <div
          className={cn(
            'mt-1 inline-flex items-center gap-1 text-xs tabular-nums',
            positive
              ? 'text-emerald-700 dark:text-emerald-300'
              : 'text-rose-600 dark:text-rose-300',
          )}
        >
          {positive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
          {Math.abs(delta).toFixed(1)}%
          {deltaSuffix && (
            <span className="text-zinc-500 dark:text-zinc-400 ml-1">{deltaSuffix}</span>
          )}
        </div>
      ) : (
        <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{subtitle}</div>
      )}
    </Card>
  );
}

function UsageBar({
  label,
  used,
  limit,
  compact = false,
}: {
  label: string;
  used: number;
  limit: number;
  compact?: boolean;
}) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-medium text-zinc-700 dark:text-zinc-200">{label}</span>
        <span className="text-xs text-zinc-500 dark:text-zinc-400 tabular-nums">
          {compact ? `${formatInt(used)} / ${formatInt(limit)}` : `${used} / ${limit}`}
        </span>
      </div>
      <div className="h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-[width] duration-500 ease-out',
            pct > 90 ? 'bg-rose-500' : pct > 75 ? 'bg-amber-500' : 'bg-brand dark:bg-brand-dm',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function QuickAction({
  to,
  icon,
  label,
}: {
  to: '/businesses/new' | '/team' | '/businesses' | '/billing' | '/subscription';
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <Link
      to={to}
      className={cn(
        'flex items-center gap-3 px-2.5 py-2 rounded-md text-sm transition',
        'text-zinc-700 dark:text-zinc-200',
        'hover:bg-zinc-50 dark:hover:bg-zinc-800',
      )}
    >
      <span className="text-zinc-500 dark:text-zinc-400">{icon}</span>
      <span className="flex-1">{label}</span>
    </Link>
  );
}
